import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

type ToastFn = (message: string) => void;

const ToastContext = createContext<ToastFn | null>(null);

export function useToast(): ToastFn {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast() used outside <ToastProvider>');
  return toast;
}

/** Single-message toast, replacing the old global-singleton DOM node
 *  (`toast.ts`) with a context provider + local state — same behavior
 *  (one message at a time, auto-hides after 2600ms, resets the timer if a
 *  new message arrives before the old one fades). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const toast = useCallback<ToastFn>((next) => {
    setMessage(next);
    setVisible(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(false), 2600);
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'pointer-events-none fixed bottom-[calc(var(--nav-h)+14px)] left-1/2 z-80 max-w-[90vw] -translate-x-1/2 translate-y-1 rounded-full bg-primary px-4 py-2.5 text-center text-sm font-medium text-primary-foreground opacity-0 shadow-lg transition-all duration-200',
          visible && 'translate-y-0 opacity-100',
        )}
      >
        {message}
      </div>
    </ToastContext.Provider>
  );
}
